import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Typography,
    useTheme,
    Grid,
    Divider,
    Paper,
} from '@mui/material';
import {
    Info as InfoIcon,
    AccessTime as AccessTimeIcon,
    MenuBook as MenuBookIcon,
    Assignment as AssignmentIcon,
    School as SchoolIcon,
    CalendarToday as CalendarTodayIcon,
    Star as StarIcon,
    GavelRounded,
    TodayRounded,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReturnBack from '../../components/reusable/accordions/ReturnBack';
import SEO from '../../components/reusable/SEO';
import { useCourses } from '../../context/CourseContext';
import parse from 'html-react-parser';
import { formatDate, formatDuration } from '../../utils/SiteHelpers';
import { DateRangeIcon } from '@mui/x-date-pickers';

function Introduction() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { courseId } = useParams();
    const { courses, updateCourseProgress } = useCourses();
    const [course, setCourse] = useState(null);

    useEffect(() => {
        if (courses.length > 0 && courseId) {
            const selectedCourse = courses.find((c) => c.slug === courseId);
            setCourse(selectedCourse);
        }
    }, [courses, courseId]);

    useEffect(() => {
        if (course) {
            updateCourseProgress(course.id, 1, 'introduction'); // use real course.id
        }
    }, [course]);

    const handleContinueToQuiz = () => {
        navigate(`/webinars/lms/${courseId}/welcome`);
    };

    if (!course) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Typography>Loading course information...</Typography>
            </Container>
        );
    }

    return (
        <Box>
            <SEO pageTitle={`${course.name} | Introduction | Toolkit.law`} />

            <ReturnBack pageTitle="Introduction" />

            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box sx={{ p: 3 }} />

                <Card sx={{ mb: 4, boxShadow: 4 }}>
                    <CardHeader
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            py: 2,
                        }}
                        title={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <InfoIcon sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Introduction
                                </Typography>
                            </Box>
                        }
                        disableTypography
                    />
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                mb: 3,
                                textAlign: 'center',
                            }}
                        >
                            {course.name}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                            {parse(course.description ?? '')}
                        </Typography>
                        {/* Course metadata cards */}
                        <Paper
                            elevation={1}
                            sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}
                        >
                            <Grid container spacing={3}>
                                {course.course_credit_hour && (
                                    <Grid item xs={12} md={6}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <StarIcon
                                                sx={{
                                                    mr: 1,
                                                    color: theme.palette.warning
                                                        .main,
                                                }}
                                            />
                                            <Typography variant="body1">
                                                <strong>CLE Credits:</strong>{' '}
                                                {course.course_credit_hour}{' '}
                                                hours
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}

                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <MenuBookIcon
                                            sx={{
                                                mr: 1,
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        />

                                        <Typography
                                            component={'span'}
                                            variant="body1"
                                        >
                                            <strong> Webinar Slides:</strong>{' '}
                                            {course.course_items_count + 1}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Course Dates */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <TodayRounded
                                            sx={{
                                                mr: 1,
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        />
                                        <Typography variant="body1">
                                            <strong>Start Date:</strong>{' '}
                                            {formatDate(
                                                course.start_date ||
                                                    '2023-09-01',
                                            )}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <DateRangeIcon
                                            sx={{
                                                mr: 1,
                                                color: theme.palette.primary
                                                    .main,
                                            }}
                                        />
                                        <Typography variant="body1">
                                            <strong>End Date:</strong>{' '}
                                            {formatDate(
                                                course.end_date || '2023-12-31',
                                            )}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 4,
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleContinueToQuiz}
                                sx={{ px: 4, py: 1.5 }}
                            >
                                Next
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

export default Introduction;
